import { ActionConfig } from "./ListBuildertypes";
import { SubQueryInterface } from "./ListBuildertypes";

export const removeSubQueryAtPath = (
   subQueries: SubQueryInterface[],
   path: string[],
): SubQueryInterface[] => {
   if (path.length === 1) return subQueries.filter((sq) => sq.parentProperty !== path[0]);

   const [head, ...rest] = path;
   return subQueries.flatMap((sq) => {
      if (sq.parentProperty !== head) return [sq];

      const newSubQueries = removeSubQueryAtPath(sq.subQueries, rest);
      const newProperties =
         rest.length === 1
            ? Object.fromEntries(Object.entries(sq.properties).filter(([key]) => key !== rest[0]))
            : sq.properties;

      if (Object.keys(newProperties).length === 0 && newSubQueries.length === 0) {
         return [];
      }

      return [{ ...sq, subQueries: newSubQueries, properties: newProperties }];
   });
};

/**
 * Returns the height of the DOM element with the ID "messages-layout".
 *
 * This function is useful when you need to dynamically calculate layout sizes
 * based on the rendered height of a specific element.
 *
 * @returns {number | null}
 * The element's height in pixels, or `null` if the element does not exist.
 */
export function getMessagesLayoutHeight() {
   const el = document.getElementById("messages-layout");
   return el ? el.offsetHeight : null;
}

/**
 * Removes keys from an object whose values are considered "empty".
 *
 * A value is treated as empty if:
 *  - It is an empty string ("")
 *  - It is `null`
 *  - It is an empty array (`[]`)
 *  - It is an empty object (`{}`)
 *
 * Useful for cleaning payloads before sending them to an API.
 *
 * @param {Record<string, any>} obj - The object to be cleaned.
 * @returns {Record<string, any>}
 * A new object containing only keys with non-empty values.
 */
export const removeEmptyFields = (obj: any) => {
   const cleaned: any = {};

   Object.entries(obj).forEach(([key, value]) => {
      const isEmpty =
         value === "" ||
         value === null ||
         (Array.isArray(value) && value.length === 0) ||
         (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);

      if (!isEmpty) cleaned[key] = value;
   });

   return cleaned;
};

/**
 * Transforms an array of model strings into a structured list of objects.
 *
 * Each model string should follow the pattern: "module.name".
 * The function splits the string into `module` and `name`, and returns
 * an array of objects formatted for use in select components or UI lists.
 *
 * @param {string[]} [models=[]] - Array of model strings in the "module.name" format.
 * @returns {{ label: string, module: string, value: string }[]}
 * A mapped array where each entry contains:
 *  - `label`: the model name,
 *  - `module`: the module name,
 *  - `value`: a combined identifier in the format "module-name".
 */
export const mapModels = (models: string[] = []) => {
   return models.map((model) => {
      const [module, name] = model.split(".");
      return {
         label: name,
         module,
         value: `${module}-${name}`,
      };
   });
};

export function reverseList<T>(list: T[]): T[] {
   return [...list].reverse();
}

// struct.actions can return 'type' instead of 'actionName' — normalise before passing to the form
export const normalizeAction = (action: Record<string, any>): ActionConfig => ({
   ...action,
   name: action.name ?? "",
   args: action.args ?? {},
   id: action.id ?? crypto.randomUUID(),
   actionName: action.actionName ?? action.type ?? "",
});
